import { SESSION_ONLY_KEY, SIGN_IN_PATHS } from './constants';
import { updateFlow } from './flow';
import { clearPersistedQueries } from 'lib/queryPersist';

/**
 * Where someone goes when their session stops working while the new sign-in is
 * on: POST Account/access-state says why (paused, removed from a team, a client
 * account, or simply signed out). No imports from the API helpers: the axios
 * instance hands its own `post` in.
 */

export type TAccessState = {
  state: 'ok' | 'account-paused' | 'team-removed' | 'client-account' | 'signed-out';
  coachName?: string | null;
  isTeamMember?: boolean;
};

type TPost = (url: string, data: unknown) => Promise<unknown>;

export const ACCESS_PATHS: Record<string, string> = {
  'account-paused': SIGN_IN_PATHS.PAUSED,
  'team-removed': SIGN_IN_PATHS.TEAM_REMOVED,
  'client-account': SIGN_IN_PATHS.CLIENT,
  'signed-out': SIGN_IN_PATHS.SIGNED_OUT,
};

/** Asks the API why the token this browser holds no longer works. Null when it can't say. */
export const askAccessState = async (post: TPost): Promise<TAccessState | null> => {
  try {
    const answer = (await post('Account/access-state', { token: localStorage.getItem('token') })) as { item?: TAccessState } | string;
    return typeof answer === 'object' && answer?.item?.state ? answer.item : null;
  } catch {
    return null;
  }
};

/** Keeps the coach's name for the screen the browser is about to show. */
export const rememberAccessState = (access: TAccessState | null): void => {
  updateFlow({ coachName: access?.coachName || null, isTeamMember: !!access?.isTeamMember });
};

/** Forgets the session on this browser (the remembered device stays), with the lists kept for it. */
export const clearStoredTokens = (): void => {
  clearPersistedQueries();
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('roles');
  localStorage.removeItem(SESSION_ONLY_KEY);
};

const AUTH_PATHS = /^\/(sign-in|forgot-password|new-password|first-log-in)(\/|$)/;

let leaving = false;

/**
 * From the axios instance: the refresh failed ('refresh-failed') or a team
 * member's coach is paused ('team-paused'). Clears the stored session and
 * sends the browser to the matching screen. Does nothing for someone who
 * never had a token, or who is already on a sign-in page.
 */
export const leaveForAccessState = async (post: TPost, reason: 'refresh-failed' | 'team-paused'): Promise<void> => {
  if (leaving || !localStorage.getItem('token') || AUTH_PATHS.test(window.location.pathname)) {
    return;
  }
  leaving = true;
  const access = await askAccessState(post);
  let path: string | null;
  if (!access) {
    path = reason === 'refresh-failed' ? SIGN_IN_PATHS.SIGNED_OUT : SIGN_IN_PATHS.PAUSED;
  } else if (access.state === 'ok') {
    // A failed refresh ends the session whatever the account's state; a 403 with an ok account is left alone.
    path = reason === 'refresh-failed' ? SIGN_IN_PATHS.SIGNED_OUT : null;
  } else {
    path = ACCESS_PATHS[access.state] || SIGN_IN_PATHS.SIGNED_OUT;
  }
  if (!path) {
    leaving = false;
    return;
  }
  rememberAccessState(access);
  clearStoredTokens();
  window.location.assign(path);
};
